<template>
  <div class="option-tile">
    <h2>{{ ratePlan.rateTypeName }} Interest Rate</h2>
    <p class="description">{{ getRateTypeDescription(ratePlan.rateTypeId) }}</p>
    <div class="rate-plan-info">
      <div class="rate-options">
        <div class="rate-and-term-wrapper">
          <div class="rate-wrapper">
            <p class="rate">
              {{ ratePlan.rateDisplay
              }}<span
                ><tooltip-component
                  class="right xs-bottom-center-right-auto"
                  v-if="ratePlan.rateTypeId === RateTypeIds.VARIABLE"
                >
                  <template #toggle>
                    <sup><icon icon="info" /></sup>
                  </template>
                  <div class="rates-tooltip">
                    <p>
                      Current offered rate is calculated using the Index, Margin and Floor value(s)
                      in effect. Your specific Interest Rate, Margin, Floor, and/or credit approval
                      depends upon the credit qualifications of the student borrower and
                      {{ coApplicantTypeDisplayName }} (if applicable).
                    </p>
                    <p>
                      The "Index" for the quarter beginning
                      {{ formatStartOfQuarter(ratePlan?.startOfQuarter) }} is
                      {{ formatRatePercentages(ratePlan?.indexRate, 3) }}, which was the Prime index
                      published in the Wall Street Journal on the first business day of
                      {{ ratePlan?.indexPublishDate }}. The credit approved margin is
                      {{ formatRatePercentages(ratePlan?.margin, 3) }}. Floor and Ceiling will be
                      disclosed at account opening.
                    </p>
                  </div>
                </tooltip-component></span
              >
            </p>
            <p>Interest Rate</p>
          </div>
          <div class="term-wrapper">
            <p class="term">
              {{ ratePlan.repaymentTerm }} year<span>
                <tooltip-component v-if="hasRepaymentTermTooltip">
                  <template #toggle>
                    <sup><icon icon="info" /></sup>
                  </template>
                  <div class="repayment-terms-tooltip">
                    <p>
                      {{ ratePlan.repaymentTermDetails?.tooltipText }}
                    </p>
                  </div>
                </tooltip-component>
              </span>
            </p>
            <p>Repayment Term</p>
          </div>
        </div>
      </div>
      <div class="rate-options">
        <div class="show-options-wrapper">
          <a class="show-options" @click="$emit('toggleRepayments')">
            Show Options <span class="caret-down"></span>
          </a>
        </div>
      </div>
    </div>
    <div
      v-show="ratePlan.paymentOptionsActive"
      class="payment-options"
      :class="{ hidden: !ratePlan.paymentOptionsActive }"
    >
      <RepaymentOption
        v-for="repayment of ratePlan.repayments"
        :key="repayment.repaymentTypeId"
        :option="repayment"
        :ratePlan="ratePlan"
        :requestedAmount="requestedAmount"
        :schoolSelected="schoolSelected"
        @update:model-value="$emit('update:repaymentType', $event)"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import usePaymentOptions from "@/use/paymentOptions";
import RepaymentOption from "@/components/loan-configuration/RepaymentOption.vue";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";
import type { RatePlanRepaymentVM, RatePlanVM, RateTypeSection } from "@/models/loanConfiguration";
import { RateTypeIds } from "@/config/constants";

const store = useStore();

const props = defineProps<{
  ratePlan: RatePlanVM;
  schoolSelected?: boolean;
  requestedAmount?: number;
}>();

const emit = defineEmits<{
  (e: "toggleRepayments"): void;
  (e: "update:repaymentType", repayment: RatePlanRepaymentVM): void;
}>();

const { formatRatePercentages, formatStartOfQuarter } = usePaymentOptions();

const loanConfigurationModule = computed(() => store.state.loanConfigurationModule);

const coApplicantTypeDisplayName = computed<string>(() =>
  store.getters["coApplicantTypeDisplayName"].toLowerCase(),
);

function getRateTypeDescription(rateTypeId: number) {
  return loanConfigurationModule.value.loanOptionsConfiguration?.rateTypeSections.find(
    (s: RateTypeSection) => s.rateTypeId === rateTypeId,
  )?.rateTypeDescription;
}

const hasRepaymentTermTooltip = computed<boolean>(() => {
  return (
    (props.ratePlan &&
      props.ratePlan?.repaymentTermDetails &&
      props.ratePlan?.repaymentTermDetails?.tooltipText !== null &&
      props.ratePlan?.repaymentTermDetails?.tooltipText !== undefined) === true
  );
});
</script>

<style scoped src="@/styles/loan-options.scss" lang="scss"></style>
